import { useCallback, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { api } from '@/lib/api-client';
import type { AuthUser, EnrollmentResult, LoginResponse } from '@/types/auth';

/**
 * Ce que la connexion a produit (#87).
 *
 * Le mot de passe seul n'ouvre plus la console : il mene soit a une demande de
 * code, soit a un enrolement, soit — s'il est faux — a une erreur.
 */
export type LoginOutcome =
  | { kind: 'signed-in' }
  | { kind: 'mfa'; hasBackupCodes: boolean }
  | { kind: 'enroll'; otpQrLink: string; enrollmentTicket: string }
  | { kind: 'error' };

interface UseAuthReturn {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    code?: string,
    loginMethod?: 'app' | 'backupCode',
  ) => Promise<LoginOutcome>;
  completeEnrollment: (enrollmentTicket: string, code: string) => Promise<string[] | null>;
  logout: () => Promise<void>;
  error: string | null;
}

export function useAuth(): UseAuthReturn {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const error = useAuthStore((s) => s.error);
  const storeLogin = useAuthStore((s) => s.login);
  const storeLogout = useAuthStore((s) => s.logout);
  const setAuthError = useAuthStore((s) => s.setAuthError);
  const clearAuthError = useAuthStore((s) => s.clearAuthError);

  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(
    async (
      email: string,
      password: string,
      code?: string,
      loginMethod: 'app' | 'backupCode' = 'app',
    ): Promise<LoginOutcome> => {
      setIsLoading(true);
      clearAuthError();
      try {
        const data = await api.post<LoginResponse>('/api/auth/login', {
          email,
          password,
          ...(code ? { code, loginMethod } : {}),
        });

        if ('enrollmentRequired' in data) {
          return {
            kind: 'enroll',
            otpQrLink: data.otpQrLink,
            enrollmentTicket: data.enrollmentTicket,
          };
        }

        if ('mfaRequired' in data) {
          return { kind: 'mfa', hasBackupCodes: data.hasBackupCodes };
        }

        storeLogin(data.user, data.accessToken, data.refreshToken);
        return { kind: 'signed-in' };
      } catch (err: unknown) {
        const apiErr = err as { statusCode?: number; message?: string | string[] };
        if (apiErr.statusCode === 401) {
          setAuthError('Invalid credentials');
        } else if (typeof apiErr.message === 'string') {
          setAuthError(apiErr.message);
        } else if (Array.isArray(apiErr.message)) {
          setAuthError(apiErr.message.join(', '));
        } else {
          setAuthError('An unexpected error occurred');
        }
        return { kind: 'error' };
      } finally {
        setIsLoading(false);
      }
    },
    [storeLogin, setAuthError, clearAuthError],
  );

  /**
   * Termine l'enrolement et ouvre la session (#87).
   *
   * Rend les codes de secours quand le serveur en emet — la seule fois ou ils
   * existent en clair. Leve si le code est refuse : l'appelant garde
   * l'operateur sur l'ecran plutot que de le renvoyer a la connexion.
   */
  const completeEnrollment = useCallback(
    async (enrollmentTicket: string, code: string): Promise<string[] | null> => {
      setIsLoading(true);
      clearAuthError();
      try {
        const data = await api.post<EnrollmentResult>('/api/auth/login/totp/enroll', {
          enrollmentTicket,
          code,
        });
        storeLogin(data.user, data.accessToken, data.refreshToken);
        return data.backupCodes;
      } finally {
        setIsLoading(false);
      }
    },
    [storeLogin, clearAuthError],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Clear local session even if server call fails (spec: error case — logout API call fails)
    }
    storeLogout();
  }, [storeLogout]);

  return {
    user,
    token,
    isAuthenticated: token !== null,
    isLoading,
    login,
    completeEnrollment,
    logout,
    error,
  };
}
